// Path: src/components/user/maintainance/bookappointmentmodal/BookAppointmentModal.jsx

import React, { useState, useEffect, useMemo, useContext } from 'react';
import { FiX, FiMapPin, FiCheck } from 'react-icons/fi';
import { GiFarmTractor } from 'react-icons/gi';
import '../../../../assets/css/user/maintainance/subscreens/bookappointment/BookAppointment.css';
import axios from 'axios';
import Swal from 'sweetalert2';
import AppContext from '../../../../context/AppContext.js';

const BookAppointment = ({ show, onHide, mechanic }) => {

  const { baseUrl } = useContext(AppContext);

  const [loading, setLoading]               = useState(false);
  const [machinerytype, setMachineryType]   = useState('');
  const [descriptionissue, setDescriptionIssue] = useState('');
  const [location, setLocation]             = useState('');
  const [errors, setErrors]                 = useState({});

  // ── Machinery Type options — dynamic, per-mechanic ────────────
  // Built from this mechanic's own `specialization` (single value,
  // chosen at registration) and `expertise` (comma-separated tags,
  // added later from their profile). Falls back to just "Other" if
  // the mechanic somehow has neither set.
  const machineryTypes = useMemo(() => {
    const options = [];

    if (mechanic?.specialization) {
      options.push(mechanic.specialization.trim());
    }

    if (mechanic?.expertise) {
      mechanic.expertise
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
        .forEach((item) => {
          if (!options.includes(item)) options.push(item);
        });
    }

    options.push('Other');
    return options;
  }, [mechanic]);

  // ── Mechanic ki booking fee (Profile se aayi hui) ────────────
  const bookingFee = Number(mechanic?.bookingfee ?? mechanic?.booking_fee ?? 0);

  // ── requestForMechanicBooking ────────────────────────────────
  // Simple flow — form submit hote hi booking seedha create ho jati hai,
  // koi payment gateway involve nahi.
  //
  // ✅ FIX: Yeh modal customer aur rental-admin dono ke mechanic-profile
  // page par reuse hota hai, lekin backend mein dono ke liye ALAG guard
  // aur ALAG route hai:
  //   - Customer  -> /customer/mechanicbooking/requestForMechanicBooking  (guard:srcustomer)
  //   - Rental admin -> /rentaladmin/maintenance/requestForBooking        (guard:srrentaladmin)
  // Pehle hamesha customer wala URL hardcoded tha, isliye rental admin
  // ka token us guard ke against 403 (Forbidden) khata tha. Ab jo bhi
  // logged in hai uske hisab se sahi endpoint aur sahi id-field chuna
  // jata hai.
  const requestForMechanicBooking = () => {
    const customer = JSON.parse(localStorage.getItem("LoggedInCustomer"));
    const rental   = JSON.parse(localStorage.getItem("LoggedInRental"));
    const token    = localStorage.getItem("authToken");

    if (!token) {
      Swal.fire({
        icon: "warning",
        title: "Login Required",
        text: "Please login first",
      });
      return;
    }

    const isRentalAdmin = Boolean(rental && !customer);

    const bookingData = {
      srmechanicadmin_id: mechanic?.id,
      machinerytype,
      descriptionissue,
      location,
      preferreddate: new Date().toISOString().split('T')[0],
      bookingfee: bookingFee,
      ...(isRentalAdmin ? { srrentaladmin_id: rental.id } : { srcustomer_id: customer?.id }),
    };

    const endpoint = isRentalAdmin
      ? `${baseUrl}/rentaladmin/maintenance/requestForBooking`
      : `${baseUrl}/customer/mechanicbooking/requestForMechanicBooking`;

    setLoading(true);

    axios
      .post(endpoint, bookingData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setLoading(false);

        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Booking Confirmed",
            text: response.data.message || "Your maintenance request has been sent.",
          });
          onHide();
          return;
        }

        Swal.fire({
          icon: "error",
          title: "Booking Failed",
          text: response.data.message || "Unable to send your request.",
        });
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);

        Swal.fire({
          title: "Error!",
          text: "Something went wrong",
          icon: "error",
          confirmButtonText: "OK",
        });
      });
  };

  // ── Reset form when modal opens ──────────────────────────────
  useEffect(() => {
    if (show) {
      setMachineryType('');
      setDescriptionIssue('');
      setLocation('');
      setErrors({});
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [show]);

  // ── Validate form ────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};

    if (!machinerytype) {
      newErrors.machinerytype = 'Please select machinery type';
    }

    if (!location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!descriptionissue.trim()) {
      newErrors.descriptionissue = 'Issue description is required';
    } else if (descriptionissue.trim().length < 10) {
      newErrors.descriptionissue = 'Description must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Handle submit ────────────────────────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    requestForMechanicBooking();
  };

  const handleCancel = () => {
    onHide();
  };

  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="bam-backdrop" onClick={handleCancel}></div>

      {/* Modal */}
      <div className="bam-modal">
        <div className="bam-modal-content">

          {/* Header */}
          <div className="bam-header">
            <div className="bam-header-content">
              <div className="bam-icon-wrapper">
                <GiFarmTractor className="bam-header-icon" />
              </div>
              <div>
                <h2 className="bam-title">Book Appointment</h2>
                <p className="bam-subtitle">
                  Schedule maintenance with {mechanic?.fullname || mechanic?.name || 'our mechanic'}
                </p>
              </div>
            </div>
            <button className="bam-close-btn" onClick={handleCancel}>
              <FiX size={24} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bam-form">
            <div className="bam-form-body">

              {/* Machinery Type */}
              <div className="bam-form-group">
                <label className="bam-label">
                  <GiFarmTractor className="bam-label-icon" />
                  Machinery Type <span className="bam-required">*</span>
                </label>
                <select
                  name="machinerytype"
                  value={machinerytype}
                  onChange={(e) => setMachineryType(e.target.value)}
                  className={`bam-select ${errors.machinerytype ? 'bam-input-error' : ''}`}
                >
                  <option value="">Select machinery type</option>
                  {machineryTypes.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </select>
                {errors.machinerytype && (
                  <span className="bam-error-text">{errors.machinerytype}</span>
                )}
              </div>

              {/* Location */}
              <div className="bam-form-group">
                <label className="bam-label">
                  <FiMapPin className="bam-label-icon" />
                  Location <span className="bam-required">*</span>
                </label>
                <input
                  type="text"
                  name="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className={`bam-input ${errors.location ? 'bam-input-error' : ''}`}
                  placeholder="Enter service location"
                />
                {errors.location && (
                  <span className="bam-error-text">{errors.location}</span>
                )}
              </div>

              {/* Description */}
              <div className="bam-form-group">
                <label className="bam-label">
                  Description <span className="bam-required">*</span>
                </label>
                <textarea
                  name="descriptionissue"
                  value={descriptionissue}
                  onChange={(e) => setDescriptionIssue(e.target.value)}
                  className={`bam-textarea ${errors.descriptionissue ? 'bam-input-error' : ''}`}
                  placeholder="Describe the issue or service required..."
                  rows="4"
                  maxLength="500"
                ></textarea>
                <div className="bam-char-count">
                  {descriptionissue.length} / 500 characters
                </div>
                {errors.descriptionissue && (
                  <span className="bam-error-text">{errors.descriptionissue}</span>
                )}
              </div>

              {/* Total Price */}
              <div className="total-box">
                <span>Booking Fee</span>
                <strong>Rs. {bookingFee.toLocaleString("en-PK")}</strong>
              </div>

            </div>

            {/* Footer */}
            <div className="bam-footer">
              <button
                type="button"
                className="bam-btn bam-btn-cancel"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bam-btn bam-btn-confirm"
                disabled={loading}
              >
                <FiCheck size={18} />
                {loading ? "Sending Request..." : "Confirm Booking"}
              </button>
            </div>

          </form>
        </div>
      </div>
    </>
  );
};

export default BookAppointment;